import React from 'react';
import { Dialog, DialogTitle, DialogContent, IconButton, Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useGetDocumentsForAnEventOfAVehicle } from '../../../../api/vehicule';
import { CustomTypography } from '../../../../components/CustomTypography/CustomTypography';
import { Icon } from '../../../../components/Icon/Icon';
import { CustomButtonTable } from '../../../../components/CustomButtonTable/CustomButtonTable';
import { colors } from '../../../../styles/colors';
import { styles } from './styles';

interface EventDocumentsModalProps {
  open: boolean;
  onClose: () => void;
  vehicleId: number;
  eventId: number;
}

export const EventDocumentsModal: React.FC<EventDocumentsModalProps> = ({ 
  open, 
  onClose, 
  vehicleId,
  eventId 
}) => {
  const { t } = useTranslation();
  const { documents, isLoading, isError } = useGetDocumentsForAnEventOfAVehicle(vehicleId, eventId);
  
  const documentList = Array.isArray(documents) ? documents : [];

  const handleDownload = (path: string, name: string) => {
    const link = document.createElement('a');
    link.href = path;
    link.download = name || 'document';
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleView = (path: string) => {
    window.open(path, '_blank', 'noopener,noreferrer');
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={styles.dialogTitle}>
          <CustomTypography
            variant="h5"
            // `total`, pas `count` : i18next traite `count` comme le
            // selecteur de pluriel et chercherait documentsTitle_one /
            // documentsTitle_other avant de retomber sur la cle de base.
            text={t("event.documentsTitle", "Attached documents ({{total}})", {
              total: documentList.length,
            })}
          />
          <IconButton
            aria-label={t("a11y.close", "Close")} onClick={onClose}>
            <Icon name="close" width="20px" height="20px" />
          </IconButton>
        </Box>
      </DialogTitle>
      
      <DialogContent>
        {isLoading ? (
          <Box sx={styles.emptyStateContainer}>
            <CustomTypography variant="default" text={t("common.loading", "Loading...")} />
          </Box>
        ) : isError ? (
          <Box sx={styles.emptyStateContainer}>
            <Icon name="file" width="48px" height="48px" />
            <CustomTypography 
              variant="default" 
              text={t("error.loadingDocuments", "Error loading documents")} 
            />
          </Box>
        ) : documentList.length === 0 ? (
          <Box sx={styles.emptyStateContainer}>
            <Icon name="file" width="48px" height="48px" />
            <CustomTypography 
              variant="default" 
              text={t("event.noDocuments", "No documents available for this event")} 
            />
          </Box>
        ) : (
          <Box>
            {documentList.map((doc) => (
              <Box key={doc.id} sx={styles.documentCard}>
                <Box sx={styles.documentInfo}>
                  <Icon
                    name="file"
                    width="24px"
                    height="24px"
                    color={colors.primary}
                  />
                  <Box sx={{ minWidth: 0 }}>
                    <Box sx={styles.documentName}>
                      <CustomTypography
                        variant="default"
                        text={doc.name || `Document ${doc.id}`}
                      />
                    </Box>
                    {doc.type && (
                      <CustomTypography
                        variant="link"
                        text={doc.type}
                        // `color="textSecondary"` etait un nom de token MUI,
                        // pas une couleur CSS : la declaration etait
                        // silencieusement ignoree par le navigateur et le
                        // sous-titre heritait d'une couleur arbitraire.
                        // CustomTypography passe cette prop telle quelle a
                        // JSS, il lui faut donc une vraie valeur.
                        color={colors.textSecondary}
                      />
                    )}
                  </Box>
                </Box>
                
                <Box sx={styles.actionButtons}>
                  {doc.path && (
                    <>
                      <CustomButtonTable
                        variant="default"
                        textVariant="link"
                        label={t("common.view", "View")}
                        iconName="eye"
                        width="fit-content"
                        onClick={() => handleView(doc.path)}
                      />
                      <CustomButtonTable
                        variant="default"
                        textVariant="link"
                        label={t("common.download", "Download")}
                        iconName="download"
                        width="fit-content"
                        onClick={() => handleDownload(doc.path, doc.name)}
                      />
                    </>
                  )}
                  {doc.scan_id && !doc.path && (
                    <CustomButtonTable
                      variant="default"
                      textVariant="link"
                      label={t("common.view", "View")}
                      iconName="eye"
                      width="fit-content"
                      onClick={() => handleView(doc.scan_id)}
                    />
                  )}
                </Box>
              </Box>
            ))}
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};